import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaHome, FaUserCircle, FaComments, FaTimes, FaExternalLinkAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const API_BASE = process.env.REACT_APP_URL;

// Adds JWT token to axios requests
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
});

export default function FeedbackPage() {
  const [feedback, setFeedback] = useState([]);
  const [error, setError] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const syncTheme = () => {
      const savedMode = localStorage.getItem("darkMode");
      setDarkMode(savedMode ? JSON.parse(savedMode) : false);
    };
    syncTheme();
    window.addEventListener("themeChanged", syncTheme);
    return () => window.removeEventListener("themeChanged", syncTheme);
  }, []);
  const [currentPage, setCurrentPage] = useState(() => {
    // Restore currentPage from localStorage, default to 1
    const saved = localStorage.getItem("feedbackCurrentPage");
    return saved ? parseInt(saved, 10) : 1;
  });
  const [pageInput, setPageInput] = useState("");
  const [showPlagModal, setShowPlagModal] = useState(false);
  const [selectedPlagMatches, setSelectedPlagMatches] = useState([]);
  const [selectedPlagScore, setSelectedPlagScore] = useState(0);
  const feedbacksPerPage = 10;
  const navigate = useNavigate();

  // Save currentPage to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("feedbackCurrentPage", currentPage.toString());
  }, [currentPage]);

  const fetchFeedback = async () => {
    try {
      const res = await axios.get(`${API_BASE}/feedback`, authConfig());
      setFeedback(res.data);
      setError("");
    } catch (err) {
      console.error("Error fetching feedback:", err);
      setError("Failed to fetch feedback from server.");
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchFeedback, 10000);
    return () => clearInterval(interval);
  }, []);

  function formatIndianDate(value) {
    if (!value) return "—";

    const date = new Date(value);

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    }).format(date);
  }

  const indexOfLastFeedback = currentPage * feedbacksPerPage;
  const indexOfFirstFeedback = indexOfLastFeedback - feedbacksPerPage;
  const currentFeedback = feedback.slice(
    indexOfFirstFeedback,
    indexOfLastFeedback
  );
  const totalPages = Math.ceil(feedback.length / feedbacksPerPage);

  // Normalize output for records coming from either Mongo (nested `output`) or
  // Prisma/Postgres (flattened columns like `output_label`, `output_confidence`, etc.)
  function normalizeOutput(record) {
    if (!record) return null;

    if (record.output) {
      try {
        return typeof record.output === "string" ? JSON.parse(record.output) : record.output;
      } catch {
        return record.output;
      }
    }

    // Prisma flattened fields
    const label = record.output_label ?? record.output_label;
    const confidence = record.output_confidence ?? record.output_confidence;
    const plagScore = record.output_plagScore ?? record.output_plagScore;

    // reconstruct possible plagMatches from mapped fields (output_plagMatches_0_phrase...)
    const plagMatches = [];
    for (let i = 0; i < 20; i++) {
      const phraseKey = `output_plagMatches_${i}_phrase`;
      const titleKey = `output_plagMatches_${i}_title`;
      const linkKey = `output_plagMatches_${i}_link`;
      if (record[phraseKey]) {
        plagMatches.push({ phrase: record[phraseKey], title: record[titleKey], link: record[linkKey] });
      }
    }

    return {
      label: label ?? undefined,
      confidence: confidence ?? undefined,
      plagScore: plagScore ?? undefined,
      plagMatches: plagMatches,
    };
  }
  return (
    <div className={darkMode ? "dark-mode" : ""} style={{
      ...styles.page,
      background: darkMode ? "transparent" : "linear-gradient(135deg, #f4f2f8, #eae6f3)"
    }}>
      <Navbar />

      <div style={{ height: "60px" }}></div>

      <div style={styles.container}>
        <div style={styles.headerContainer}>
          <button style={styles.backButton} onClick={() => navigate("/admin")}>
            ⮜
          </button>

          <div style={styles.titleSection}>
            <FaComments size={24} style={{ marginRight: "8px", color: "#636CCB" }} />
            <h1 style={styles.title}>Feedback List</h1>
          </div>

          <div style={{ width: styles.backButton.width || 'auto', visibility: 'hidden' }}></div>
        </div>

        <div style={styles.card}>
          {error ? (
            <p style={{ color: "red", textAlign: "center" }}>{error}</p>
          ) : currentFeedback.length > 0 ? (
            <>
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={styles.thCenter}>Sl. No</th>
                      <th style={styles.thLeft}>Username</th>
                      <th style={styles.thLeft}>Rating</th>
                      <th style={styles.thLeft}>Comment</th>
                      <th style={styles.thLeft}>Input</th>
                      <th style={styles.thLeft}>AI Output</th>
                      <th style={styles.thLeft}>Plagiarism</th>
                      <th style={styles.thLeft}>Created</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentFeedback.map((f, index) => (
                      <tr
                        key={f.id || f._id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "var(--zebra-stripe)" : "transparent",
                          borderBottom: "1px solid var(--border-light)",
                        }}
                      >
                        <td style={styles.cellCenter}>
                          {indexOfFirstFeedback + index + 1}
                        </td>

                        <td style={styles.cellLeftBold}>{f.username}</td>

                        <td style={styles.cellLeft}>
                          {f.rating === 5 ? (
                            <span style={{ color: "green", fontWeight: "600" }}>
                              Liked
                            </span>
                          ) : f.rating === 1 ? (
                            <span style={{ color: "red", fontWeight: "600" }}>
                              Disliked
                            </span>
                          ) : (
                            <span style={{ color: "#eab308", fontWeight: "600" }}>
                              Neutral
                            </span>
                          )}
                        </td>

                        <td style={styles.cellTruncate}>{f.comment || "—"}</td>

                        <td style={styles.cellLeft}>
                          {f.inputType === "text" ? (
                            <button
                              style={styles.viewButton}
                              onClick={() =>
                                navigate(`/view-input/${f.id || f._id}`, {
                                  state: { type: "text" },
                                })
                              }
                            >
                              View Text
                            </button>
                          ) : f.inputType === "file" ? (
                            <button
                              style={styles.viewButton}
                              onClick={() =>
                                navigate(`/view-input/${f.id || f._id}`, {
                                  state: { type: "file" },
                                })
                              }
                            >
                              View File
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td style={{ ...styles.cellLeft, ...styles.cellTruncate }}>
                          {(() => {
                            const out = normalizeOutput(f);
                            if (!out) return "—";
                            return `${out.label ?? "—"} — ${out.confidence ?? "—"}%`;
                          })()}
                        </td>

                        <td style={styles.cellLeft}>
                          {(() => {
                            const out = normalizeOutput(f);
                            if (!out) return "—";
                            const score = out.plagScore !== undefined ? out.plagScore : "—";
                            const matches = out.plagMatches || [];

                            return (
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{
                                  color: score > 0 ? "#ef4444" : "#22c55e",
                                  fontWeight: "600"
                                }}>
                                  {score !== "—" ? `${score}%` : "—"}
                                </span>
                                {matches.length > 0 && (
                                  <button
                                    style={styles.viewButtonSmall}
                                    onClick={() => {
                                      setSelectedPlagMatches(matches);
                                      setSelectedPlagScore(score);
                                      setShowPlagModal(true);
                                    }}
                                  >
                                    Matches
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>

                        <td style={styles.cellLeft}>
                          {formatIndianDate(f.createdAt)}
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={styles.pagination}>
                {/* First Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === 1 ? 0.4 : 1,
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  title="First Page"
                >
                  «
                </button>

                {/* Previous Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === 1 ? 0.4 : 1,
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  title="Previous Page"
                >
                  ‹
                </button>

                {/* Page Input */}
                <div style={styles.pageInputContainer}>
                  <span style={styles.pageLabel}>Page</span>
                  <input
                    type="number"
                    value={pageInput || currentPage}
                    onChange={(e) => setPageInput(e.target.value)}
                    onBlur={() => {
                      const num = parseInt(pageInput, 10);
                      if (num >= 1 && num <= totalPages) {
                        setCurrentPage(num);
                      }
                      setPageInput("");
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        const num = parseInt(pageInput, 10);
                        if (num >= 1 && num <= totalPages) {
                          setCurrentPage(num);
                        }
                        setPageInput("");
                      }
                    }}
                    style={styles.pageInput}
                    min="1"
                    max={totalPages}
                  />
                  <span style={styles.pageLabel}>of {totalPages}</span>
                </div>

                {/* Next Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === totalPages ? 0.4 : 1,
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  title="Next Page"
                >
                  ›
                </button>

                {/* Last Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === totalPages ? 0.4 : 1,
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  title="Last Page"
                >
                  »
                </button>
              </div>
            </>
          ) : (
            <p style={styles.noData}>No feedback available</p>
          )}
        </div>
      </div>

      {showPlagModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Plagiarism Matches ({selectedPlagScore}%)</h2>
              <FaTimes
                style={styles.closeIcon}
                onClick={() => setShowPlagModal(false)}
              />
            </div>
            <div style={styles.modalBody}>
              {selectedPlagMatches.length > 0 ? (
                selectedPlagMatches.map((m, i) => (
                  <div key={i} style={styles.matchItem}>
                    <p style={styles.matchText}>"{m.phrase}"</p>
                    <a
                      href={`${m.link}#:~:text=${(() => {
                        const words = m.phrase.split(/\s+/);
                        if (words.length > 8) {
                          const start = words.slice(0, 4).join(' ');
                          const end = words.slice(-4).join(' ');
                          return `${encodeURIComponent(start)},${encodeURIComponent(end)}`;
                        }
                        return encodeURIComponent(m.phrase);
                      })()}`}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.sourceLink}
                    >
                      Source Link <FaExternalLinkAlt size={12} style={{ marginLeft: "4px" }} />
                    </a>
                  </div>
                ))
              ) : (
                <p>No matches found.</p>
              )}
            </div>
          </div>
        </div>
      )}

      <footer style={styles.footer}>
        Navriti Technologies © All rights reserved
      </footer>
    </div>
  );
}

const styles = {
  page: {
    fontFamily: "Poppins, sans-serif",
    minHeight: "100vh",
    paddingBottom: "80px",
  },

  footer: {
    width: "100%",
    backgroundColor: "#636CCB",
    color: "white",
    textAlign: "center",
    height: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "600",
    fontSize: "1em",
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: 2000,
  },

  backButton: {
    backgroundColor: "#636CCB",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: "1.2em",
    width: "60px",
  },

  container: {
    padding: "10px 30px 40px",
  },

  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    padding: "20px 0 0",
  },

  titleSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  title: { fontSize: "1.5em", fontWeight: "700", color: "var(--text-main)", margin: 0 },

  card: {
    background: "var(--card-bg)",
    borderRadius: "12px",
    padding: "15px",
    transition: "background 0.3s, color 0.3s",
  },

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.9em",
  },
  tableHeader: {
    backgroundColor: "#636CCB",
    color: "white",
    textAlign: "left",
  },


  thLeft: { textAlign: "left", padding: "10px" },
  thCenter: { textAlign: "center", padding: "10px" },
  thRight: { textAlign: "right", padding: "10px" },

  cellCenter: { textAlign: "center", padding: "8px", color: "var(--text-main)" },
  cellLeft: { textAlign: "left", padding: "8px 10px", color: "var(--text-main)" },
  cellRight: { textAlign: "right", padding: "8px 10px", color: "var(--text-main)" },
  cellLeftBold: { fontWeight: "600", padding: "8px 10px", color: "var(--text-main)" },

  cellTruncate: {
    maxWidth: "200px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "var(--text-main)",
  },

  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    marginTop: "15px",
  },

  navButton: {
    border: "none",
    padding: "10px 16px",
    backgroundColor: "var(--text-main)",
    color: "var(--card-bg)",
    borderRadius: "20px",
    cursor: "pointer",
    fontSize: "1.2em",
    fontWeight: "600",
    minWidth: "45px",
    transition: "opacity 0.2s",
  },

  pageInputContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "var(--card-bg)",
    border: "2px solid var(--border-light)",
    padding: "8px 16px",
    borderRadius: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },

  pageLabel: {
    fontSize: "1em",
    fontWeight: "600",
    color: "var(--text-main)",
  },

  pageInput: {
    width: "50px",
    padding: "4px 8px",
    border: "2px solid #3b82f6",
    borderRadius: "6px",
    textAlign: "center",
    fontSize: "1em",
    fontWeight: "600",
    outline: "none",
    backgroundColor: "var(--card-bg)",
    color: "var(--text-main)",
  },

  viewButton: {
    backgroundColor: "#636CCB",
    color: "white",
    padding: "5px 10px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
  },

  viewButtonSmall: {
    backgroundColor: "#636CCB",
    color: "white",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontSize: "0.85em",
  },

  noData: { textAlign: "center", padding: "20px", color: "var(--text-sub)" },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 3000,
  },

  modalContent: {
    backgroundColor: "var(--card-bg)",
    color: "var(--text-main)",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "600px",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
  },

  modalHeader: {
    padding: "15px 20px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  modalTitle: {
    margin: 0,
    fontSize: "1.2em",
    color: "var(--text-main)",
  },

  closeIcon: {
    cursor: "pointer",
    fontSize: "1.2em",
    color: "#666",
  },

  modalBody: {
    padding: "20px",
    overflowY: "auto",
  },

  matchItem: {
    marginBottom: "15px",
    padding: "12px",
    backgroundColor: "var(--zebra-stripe)",
    borderRadius: "8px",
    borderLeft: "4px solid #ef4444",
  },

  matchText: {
    margin: "0 0 8px 0",
    fontSize: "0.95em",
    fontStyle: "italic",
    color: "var(--text-main)",
  },

  sourceLink: {
    color: "#636CCB",
    textDecoration: "none",
    fontSize: "0.85em",
    fontWeight: "600",
    display: "inline-flex",
    alignItems: "center",
  },
};